import { useState, useEffect } from 'react'
import { Box, Tooltip, Typography, Paper, Stack, Chip } from '@mui/material'
import { styled } from '@mui/material/styles'
import { useSmartTranslation } from './hooks/useSmartTranslation'
import { systemValueLabel } from './systemValues/systemValueLabel'

const StatusSection = styled(Box)(({ theme, isHovered }) => ({
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  transition: 'all 0.2s ease-in-out',
  transform: isHovered ? 'scale(1.05)' : 'scale(1)',
  filter: isHovered ? 'brightness(1.2)' : 'brightness(1)',
  boxShadow: isHovered ? '0 0 15px rgba(0,0,0,0.3)' : 'none',
  zIndex: isHovered ? 10 : 1,
  position: 'relative'
}))

// Story 204 — S73: `domain` is the enum the bar's codes belong to; each code and status is shown by its translation.
const StatusBar = ({ dataFetch, sections, legend = false, showCount = false, height = 24, domain = 'PartStatusEnum' }) => {
  const { t_ } = useSmartTranslation()
  const [hoveredIndex, setHoveredIndex] = useState(null)
  const [data, setData] = useState([])
  const [firstLoad, setFirstLoad] = useState(true)

  useEffect(() => {
    const getData = async () => {
      const statusData = await dataFetch()
      const mergedData = statusData.map((item) => ({
        ...item,
        color: sections.find(s => s.code === item.code)?.color
      }))
      setData(mergedData)
      setFirstLoad(false)
    }
    if (firstLoad) {
      getData()
    }
  }, [dataFetch, sections, firstLoad])

  const total = data.reduce((sum, item) => sum + item.count, 0)

  return (
    <Box sx={{ width: '100%', maxWidth: 800, mx: 'auto', p: 4 }}>
      <Paper elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', height, position: 'relative' }}>
          {data.map((item, index) => {
            const width = (item.count / total) * 100
            const isHovered = hoveredIndex === index

            return (
              <Tooltip
                key={index}
                title={
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant='body2' sx={{ fontWeight: 600 }}>
                      {systemValueLabel(t_, domain, item.code)}
                    </Typography>
                    <Typography variant='caption'>
                      {item.count}
                    </Typography>
                  </Box>
                }
                arrow
                placement='bottom'
                open={isHovered}
                disableFocusListener
                disableTouchListener
              >
                <StatusSection
                  isHovered={isHovered}
                  sx={{
                    width: `${width}%`,
                    backgroundColor: item.color
                  }}
                  onMouseEnter={() => setHoveredIndex(index)}
                  onMouseLeave={() => setHoveredIndex(null)}
                >
                  <Typography
                    variant='body2'
                    sx={{
                      color: 'white',
                      fontWeight: 600,
                      textShadow: '0 1px 3px rgba(0,0,0,0.3)'
                    }}
                  >
                    {showCount ? item.count : ''}
                  </Typography>
                </StatusSection>
              </Tooltip>
            )
          })}
        </Box>
      </Paper>
      {legend
        ? (
          <Stack
            direction='row'
            spacing={2}
            sx={{ mt: 3, flexWrap: 'wrap', justifyContent: 'center', gap: 1 }}
          >
            {data.map((item, index) => (
              <Chip
                key={index}
                label={systemValueLabel(t_, domain, item.status || item.code)}
                sx={{
                  backgroundColor: item.color,
                  color: 'white',
                  fontWeight: 500,
                  '& .MuiChip-label': {
                    px: 2
                  }
                }}
              />
            ))}
          </Stack>
          )
        : null}

    </Box>
  )
}

export default StatusBar
